import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import CloseIcon from '@mui/icons-material/Close';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import AddIcon from '@mui/icons-material/Add';
import {
  harmonateDrawerHeaderSx,
  harmonateDialogCloseIconSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogFormContentSx,
  harmonateDataGridSx,
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateUploadOutlinedSx,
  harmonateFormFieldsSx,
  harmonateTextFieldSx,
  harmonateInputLabelProps,
  harmonateSelectMenuProps,
} from '../../../theme/harmonateStyles';
import { useFunctionPermissions } from '../../../hooks/useFunctionPermissions';
import { FC } from '../../../constants/functionCodes';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';

const harmonateChipSx = (isYes) => ({
  bgcolor: isYes ? ht.secondaryTab : ht.listBg,
  color: isYes ? ht.text : ht.text,
  fontWeight: 500,
  fontSize: '0.75rem',
  height: 24,
  '& .MuiChip-label': { px: 1.5 },
});

const GlobalUserDrawer = ({ open, onClose, userId }) => {
  const { canAdd } = useFunctionPermissions(FC.GLOBAL_USERS);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  
  // Add tenant functionality state
  const [addTenantDialogOpen, setAddTenantDialogOpen] = useState(false);
  const [availableTenants, setAvailableTenants] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [addingTenant, setAddingTenant] = useState(false);

  // Fetch user's associated tenants
  const fetchUserTenants = async () => {
    if (!userId) return;
    
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      
      if (!token) throw new Error('No authentication token found');

      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/users/${userId}/tenants`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!resp.ok) {
        throw new Error(`Failed to fetch user tenants: ${resp.status} ${resp.statusText}`);
      }
      
      const data = await resp.json();
      setTenants(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message);
      setTenants([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch user details for display
  const fetchUserDetails = async () => {
    if (!userId) return;
    
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/users/${userId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (resp.ok) {
        const data = await resp.json();
        setUserDetails(data);
      }
    } catch (err) {
      console.error('Error fetching user details:', err);
    }
  };

  // Fetch all available tenants for the dropdown
  const fetchAvailableTenants = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/tenants`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!resp.ok) throw new Error('Failed to fetch tenants');
      
      const data = await resp.json();
      
      // Filter out tenants that are already assigned to the user
      const assignedTenantIds = tenants.map(tenant => tenant.id);
      const filteredTenants = data.filter(tenant => !assignedTenantIds.includes(tenant.id));
      
      setAvailableTenants(Array.isArray(filteredTenants) ? filteredTenants : []);
    } catch (err) {
      console.error('Error fetching available tenants:', err);
      setAvailableTenants([]);
    }
  };

  useEffect(() => {
    if (open && userId) {
      fetchUserTenants();
      fetchUserDetails();
    }
  }, [open, userId]);

  const handleClose = () => {
    setTenants([]);
    setUserDetails(null);
    setError(null);
    setAddTenantDialogOpen(false);
    setSelectedTenantId('');
    setAvailableTenants([]);
    onClose();
  };

  // Add tenant dialog handlers
  const handleOpenAddTenantDialog = () => {
    if (!canAdd) return;
    setAddTenantDialogOpen(true);
    fetchAvailableTenants();
  };

  const handleCloseAddTenantDialog = () => {
    setAddTenantDialogOpen(false);
    setSelectedTenantId('');
    setAvailableTenants([]);
  };

  const handleAddTenant = async () => {
    if (!selectedTenantId || !userId) return;
    
    setAddingTenant(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/user-tenants`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: userId,
          tenantId: selectedTenantId
        }),
      });

      if (!resp.ok) {
        const errorData = await resp.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to assign tenant to user');
      }

      // Refresh the tenants list
      await fetchUserTenants();
      handleCloseAddTenantDialog();
      
      // You can add a success toast here if you have toast functionality
      console.log('Tenant assigned successfully');
    } catch (err) {
      console.error('Error assigning tenant:', err);
      // You can add an error toast here if you have toast functionality
    } finally {
      setAddingTenant(false);
    }
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Tenant Name',
      flex: 1,
      minWidth: 200
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => {
        const isActive = (params.value || 'ACTIVE') === 'ACTIVE';
        return (
          <Chip
            label={params.value || 'ACTIVE'}
            size="small"
            sx={harmonateChipSx(isActive)}
          />
        );
      }
    }
  ];

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: 500,
          p: 0,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Box sx={{ ...harmonateDrawerHeaderSx, mx: 0, mt: 0, borderRadius: 0 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
          <PersonIcon />
          User Details
        </Typography>
        <IconButton onClick={handleClose} size="small" sx={harmonateDialogCloseIconSx}>
          <CloseIcon />
        </IconButton>
      </Box>

      {userDetails && (
        <Box sx={{ p: 2, borderBottom: `1px solid ${ht.line}` }}>
          <Typography variant="body1" sx={{ fontWeight: 500, color: ht.text }}>
            {userDetails.first_name} {userDetails.last_name}
          </Typography>
          <Typography variant="body2" sx={{ color: ht.text }}>
            {userDetails.email}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <Chip
              label={userDetails.status === 1 ? 'Active' : 'Inactive'}
              size="small"
              sx={harmonateChipSx(userDetails.status === 1)}
            />
            <Chip
              label={userDetails.harmonate_user ? 'Harmonate User' : 'Regular User'}
              size="small"
              sx={harmonateChipSx(!!userDetails.harmonate_user)}
            />
          </Box>
        </Box>
      )}

      <Box sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1, color: ht.text }}>
            <BusinessIcon sx={{ color: ht.text }} />
            Associated Tenants ({tenants.length})
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleOpenAddTenantDialog}
            disabled={!canAdd}
            sx={harmonateUploadOutlinedSx}
          >
            Add Tenant
          </Button>
        </Box>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress size={24} sx={{ color: ht.blue }} />
            <Typography variant="body2" sx={{ ml: 2, color: ht.text }}>Loading tenants...</Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && (
          <Paper elevation={0} sx={{ flex: 1, borderRadius: 2, overflow: 'hidden', minHeight: 300 }}>
            <DataGridPro
              rowHeight={35}
              rows={tenants.map((tenant, idx) => ({
                id: tenant.id || idx + 1,
                name: tenant.name || 'Unknown Tenant',
                status: tenant.status || 'ACTIVE'
              }))}
              columns={columns}
              columnResizeMode="onEnd"
              resizable
              sx={{
                ...harmonateDataGridSx,
                flex: 1,
                '& .MuiDataGrid-cell': {
                  fontSize: '0.75rem',
                  color: ht.text,
                },
                '& .MuiDataGrid-row:hover': { bgcolor: ht.listBg },
              }}
              disableRowSelectionOnClick
              hideFooter={tenants.length <= 10}
              getRowId={(row) => row.id}
              slots={{
                noRowsOverlay: () => (
                  <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', py: 4 }}>
                    <BusinessIcon sx={{ fontSize: 48, color: ht.fadedText, mb: 1 }} />
                    <Typography variant="body1" sx={{ color: ht.text }}>
                      No tenants associated
                    </Typography>
                    <Typography variant="body2" sx={{ color: ht.text }}>
                      This user is not assigned to any tenants
                    </Typography>
                  </Box>
                )
              }}
            />
          </Paper>
        )}
      </Box>

      {/* Add Tenant Dialog */}
      <Dialog open={addTenantDialogOpen} onClose={handleCloseAddTenantDialog} maxWidth="sm" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>Add Tenant to User</DialogTitle>
        <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx }}>
          <FormControl fullWidth size="small" sx={harmonateTextFieldSx}>
            <InputLabel {...harmonateInputLabelProps}>Select Tenant</InputLabel>
            <Select
              value={selectedTenantId}
              onChange={(e) => setSelectedTenantId(e.target.value)}
              label="Select Tenant"
              disabled={addingTenant}
              MenuProps={harmonateSelectMenuProps}
            >
              {availableTenants.length === 0 ? (
                <MenuItem value="" disabled>
                  No available tenants
                </MenuItem>
              ) : (
                availableTenants.map((tenant) => (
                  <MenuItem key={tenant.id} value={tenant.id}>
                    {tenant.name}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseAddTenantDialog} disabled={addingTenant} variant="outlined" sx={harmonateCancelButtonSx}>
            Cancel
          </Button>
          <Button
            onClick={handleAddTenant}
            variant="contained"
            disabled={!selectedTenantId || addingTenant}
            sx={harmonateProceedButtonSx}
          >
            {addingTenant ? 'Adding...' : 'Add Tenant'}
          </Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  );
};

export default GlobalUserDrawer;
